import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import { ShoppingBag } from 'lucide-react';
import { CreateBookingDTO } from '../../../../types/booking';
import { useServices } from '../../../../hooks/service/useServices';
import SectionTitle from './SectionTitle';

interface ServiceSectionProps {
  control: Control<CreateBookingDTO>;
  resourceId: number;
  disabled?: boolean;
}

const ServiceSection: React.FC<ServiceSectionProps> = ({
  control,
  resourceId,
  disabled = false,
}) => {
  const theme = useTheme();
  const { services } = useServices();

  const availableServices = services.filter(service =>
    service.serviceResourceMapping.some(mapping => mapping.resources.id === resourceId)
  );

  return (
    <div>
      <SectionTitle icon={ShoppingBag} title="Select Services" />
      <Controller
        name="serviceId"
        control={control}
        rules={{ required: 'At least one service is required' }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <InputLabel>Select Services</InputLabel>
            <Select
              value={value || ''}
              onChange={event => {
                // Convert the single value to an array
                const selectedValue = event.target.value;
                onChange(Array.isArray(selectedValue) ? selectedValue : [selectedValue]);
              }}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(Array.isArray(selected) ? selected : [selected]).map((serviceId: number) => {
                    const service = availableServices.find(s => s.id === serviceId);
                    return service ? <Chip key={serviceId} label={service.name} /> : null;
                  })}
                </Box>
              )}
              disabled={!resourceId || disabled}
            >
              {availableServices.map(service => (
                <MenuItem key={service.id} value={service.id}>
                  <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1">{service.name}</Typography>
                      <Chip
                        label={`${service.duration} min`}
                        size="small"
                        sx={{
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
            {!resourceId && <FormHelperText>Please select a resource first</FormHelperText>}
          </FormControl>
        )}
      />
    </div>
  );
};

export default ServiceSection;
